/**
 * Design System Tokens — единый источник истины для всех цветов, радиусов и теней.
 *
 * Используется в:
 * - ThemeManager.tsx (MUI createTheme)
 * - Storybook docs (визуальная документация)
 * - Компоненты (через theme.palette или прямой импорт для edge-cases)
 */

/**
 * Ядро дизайн-системы приходит из `@aspid/ui` (AF-23): семантические цвета,
 * шкала теней и радиусы — 18 значений, доказуемо одинаковых во всех трёх
 * приложениях. Здесь остаётся только своё.
 *
 * Импорты по всему приложению не менялись: этот файл по-прежнему экспортирует
 * `palette`, `shadows`, `shape` с тем же содержимым. Реэкспорт — механика
 * подъёма, а не решение: он маскирует расхождение, потому что импорт выглядит
 * локальным, а часть значений резолвится в пакет. Долг снимается вместе с
 * AF-43, когда раскладка сведётся.
 */
import { colorSchemes, shadows as coreShadows, radii } from '@aspid/ui';

// ============================================================
// PALETTE
// ============================================================

/**
 * `palette` — единственный источник значений (AF-43).
 *
 * До этого значения были поделены между `palette` (семантика) и `lightPalette`
 * (поверхности), и у токена было два возможных адреса: чтобы узнать, где живёт
 * `border.default`, надо было помнить соглашение. У deals такого деления нет —
 * там `palette` полный источник, а `lightPalette` его проекция, — и владелец
 * выбрал эту форму для всех трёх.
 *
 * Довод, решивший выбор: `colorSchemes.light` из `@aspid/ui` вливается сюда
 * через spread ниже. Пока поверхности лежали в отдельном объекте, они этим
 * путём не питались, и подъём их в пакет потребовал бы второй, другой дороги.
 * Теперь семантика и поверхности стоят в одной позиции.
 */
export const palette = {
  ...colorSchemes.light,
  secondary: {
    /**
    Розовый акцент проекта.

    НЕ поднят в `@aspid/ui` намеренно: у deals и analyst здесь серый `#757575`,
    и это единственный ключ, который есть у всех трёх и расходится. Серый
    против розового — вопрос идентичности, а не расхождение; порога, который
    бы его рассудил, не существует (AF-23).
    */
    main: '#F291A3',
  },
  background: {
    /**
    Monochrome: нейтральный серый фон
    */
    default: '#FAFAFA',
    paper: '#FFFFFF',
  },
  text: {
    /**
    Monochrome: чистый тёмный, без синего оттенка
    */
    primary: '#1A1A1A',
    secondary: '#555555',
    disabled: '#888888',
    /*
     * `muted` — приглушённый, но ЧИТАЕМЫЙ текст (AF-67).
     *
     * Появился потому, что роль «третий уровень приглушения» существовала и
     * раньше, только выражалась токеном `disabled`: по замеру 142 вхождения
     * красили им содержимое, которое пользователь обязан прочесть.
     *
     *   disabled  #888888  3.54:1   ниже порога AA 4.5 для текста
     *   muted     #757575  4.61:1   проходит, и заметно светлее secondary
     *   secondary #555555  7.46:1
     *
     * Почему отдельный токен, а не `secondary`. В AF-65 третий токен был
     * отвергнут — но там речь шла о трёх ссылках, которым `secondary`
     * подходил по смыслу. Здесь замер другой: в 81 файле из 122 `secondary` и
     * `disabled` встречаются ВМЕСТЕ, то есть два уровня приглушения заданы
     * намеренно. Свести их значило бы убрать уровень оформления ради порога,
     * а не привести оформление в соответствие с порогом.
     *
     * `disabled` остаётся `#888888` для настоящей неактивности (12 вхождений):
     * там приглушённость и есть смысл, и WCAG 1.4.3 такие элементы исключает.
     */
    muted: '#757575',
  },
  border: {
    /**
    Monochrome: нейтральные серые границы
    */
    default: '#E0E0E0',
    light: '#F0F0F0',
    focus: '#BDBDBD',
  },
} as const;

// ============================================================
// LIGHT MODE
// ============================================================

/**
 * Проекция `palette`, а не второй набор значений.
 *
 * Форма сохранена ровно та, что была: те же ключи, те же значения — поэтому ни
 * один из потребителей не правится. Литералов здесь нет намеренно: появившийся
 * тут литерал означает значение, которого нет в `palette`, то есть возврат к
 * двум адресам у одного токена.
 */
export const lightPalette = {
  background: palette.background,
  text: palette.text,
  border: palette.border,
  divider: palette.border.default,
} as const;

// ============================================================
// DARK MODE — переопределения поверх MUI dark defaults
// ============================================================

export const darkPalette = {
  divider: 'rgba(255, 255, 255, 0.12)',
  /**
   * Тёмный аналог `text` — ровно один ключ, и только потому, что MUI своего
   * не знает (AF-575/AF-576).
   *
   * `primary`, `secondary` и `disabled` в тёмной теме выводит сам MUI, и они
   * здесь намеренно не переопределяются. `muted` — наш токен (AF-67), MUI о
   * нём не слышал, поэтому в тёмной теме он оставался `undefined`: `color:
   * 'text.muted'` не резолвился, CSS значение отбрасывал, элемент наследовал
   * цвет родителя. Молча — ни предупреждения, ни падения.
   *
   * ─── Почему 0.6 ────────────────────────────────────────────────────────
   *
   * Значение продолжает лестницу MUI, а не выбрано на глаз. Решение владельца
   * 27.09.2026: «чинить в соответствии с MUI». Контраст посчитан на дефолтном
   * тёмном фоне `#121212`:
   *
   *     secondary  rgba(255, 255, 255, 0.7)  #B8B8B8   9.44:1   дефолт MUI
   *     muted      rgba(255, 255, 255, 0.6)  #A0A0A0   7.16:1   этот токен
   *     disabled   rgba(255, 255, 255, 0.5)  #888888   5.28:1   дефолт MUI
   *
   * Порядок приглушения тот же, что в светлой теме (secondary → muted →
   * disabled), и порог AA 4.5 проходит с запасом — как и `#757575` в светлой
   * (4.61:1), ради чего токен и заводился.
   *
   * ─── Отвергнуто: взять светлое `#757575` ───────────────────────────────
   *
   * На `#121212` оно даёт 4.02:1 — ниже AA. Токен, заведённый ради порога, не
   * может его нарушать во втором режиме.
   *
   * ─── Отвергнуто: спред `...lightPalette` в обе ветки, как у deals ──────
   *
   * Так `muted` появился бы, но вместе с ним в тёмную тему уехали бы СВЕТЛЫЕ
   * цвета текста: у deals `createAppTheme('dark').palette.text.primary` равен
   * `#1A1A1A` — почти чёрный на тёмном фоне. Это дефект deals, а не образец.
   */
  text: {
    muted: 'rgba(255, 255, 255, 0.6)',
  },
} as const;

// ============================================================
// TYPOGRAPHY
// ============================================================

export const typography = {
  fontFamily: [
    '"Inter"',
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'sans-serif',
  ].join(','),
  /**
  Моноширинный шрифт для числовых значений, score-pills, code
  */
  monoFontFamily: ['"JetBrains Mono"', '"DM Mono"', 'monospace'].join(','),
  fontSize: 14,
  /**
  Section labels: uppercase мини-заголовки (Swiss-стиль)
  */
  sectionLabel: {
    fontSize: '0.6875rem',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.1em',
  },
} as const;

// ============================================================
// SHAPE & RADIUS
// ============================================================

export const shape = {
  ...radii,
  /**
  Карточки, Paper, Dialog — Swiss-Monochrome стиль
  */
  /**
  Кнопки
  */
  /**
  Инпуты
  */
} as const;

// ============================================================
// SHADOWS
// ============================================================

export const shadows = {
  ...coreShadows,
  /**
  Swiss-стиль: минимальные тени, предпочтение border
  */
  /**
  Focus ring — navy accent
  */
} as const;

// ============================================================
// BORDERS — Swiss-стиль: border-first вместо shadow-first
// ============================================================

export const borders = {
  /**
  Основной border для карточек, Paper
  */
  default: `1px solid ${lightPalette.border.default}`,
} as const;

